/* eslint-disable eslint-comments/no-restricted-disable */

/* eslint-disable eslint-comments/no-restricted-disable */

import React, { useCallback, useMemo, useRef } from 'react';
import { useThemeObserver } from '@atlaskit/tokens';
import { EMBEDDED_CONFLUENCE_MODE, parseUrl } from '@atlassian/embedded-confluence';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useEmbeddedConfluenceSidePanel } from '@jira/confluence__confluence-integration-controls/src/controllers/use-embedded-confluence-side-panel/index.tsx';
import { convertUrlByContentState } from '@jira/confluence__confluence-integration-controls/src/controllers/use-embedded-confluence-side-panel/utils.tsx';
import { JiraEntryPointContainer } from '@jira/platform__entry-points-container/src/index.tsx';

// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import { useEntryPoint } from '@jira/platform__entry-point/src/controllers/use-entry-point/index.tsx';

import { useIntl } from '@atlassian/jira-intl';

import type { ConfluenceData } from '@jira/confluence__confluence-integration-controls/src/controllers/use-embedded-confluence-side-panel/types.tsx';
// eslint-disable-next-line jira/restricted/@atlassian+jira-entry-point
import type { EntryPointActions } from '@jira/platform__entry-point/src/controllers/utils/types.tsx';
import { embeddedConfluencePageModalEntrypoint } from './embeddedConfluencePageModalEntrypoint.entrypoint.tsx';
import { CONTENT_TYPE } from './EmbeddedPageModalProps.types.tsx';
import { draftEmbeddedPageModalEntrypoint } from './draftEmbeddedPageModalEntrypoint.entrypoint.tsx';
import { embeddedPanelEntrypoint } from './embeddedPanelEntrypoint.entrypoint.tsx';
import { setHostnameForContent } from './constructUrlIfCustomDomain.tsx';
import { useEmbeddedPageDraft } from './useEmbeddedPageDraft.tsx';
import { useEmbeddedPageTracking } from './useEmbeddedPageTracking.tsx';
import { useParentProduct } from './useParentProduct.tsx';
import { embeddedWhiteboardModalEntrypoint } from './embeddedWhiteboardModalEntrypoint.entrypoint.tsx';

const entryPointParams = {};
// exported for testing
export const OPERATIONAL_EVENT_TYPE = 'operational';

interface HandleContentActionParams {
	fireAnalyticsEvent: (event: {
		action: string;
		eventName: string;
		embeddedConfluenceSource: string;
		eventType: string;
	}) => void;
	setConfluenceData: (arg: ConfluenceData) => void;
	contentUrl: string;
	embeddedConfluenceSourceForPanel: string;
	isOpen: boolean;
	mode: EMBEDDED_CONFLUENCE_MODE | undefined;
	havePublishedRef: React.MutableRefObject<boolean>;
}

export const handleContentAction = (
	action: string,
	eventName: string,
	params: HandleContentActionParams,
): void => {
	const {
		fireAnalyticsEvent,
		setConfluenceData,
		contentUrl,
		embeddedConfluenceSourceForPanel,
		isOpen,
		mode,
		havePublishedRef,
	} = params;
	fireAnalyticsEvent({
		action,
		eventName,
		embeddedConfluenceSource: embeddedConfluenceSourceForPanel,
		eventType: OPERATIONAL_EVENT_TYPE,
	});

	if (isOpen) {
		if (eventName === 'edit-page/close') {
			setConfluenceData({
				contentUrl,
				mode: EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
			});
		}

		if (eventName === 'edit-page/load' && action === 'taskSuccess') {
			if (
				havePublishedRef.current &&
				expValEquals('cc_jira_cppp_publish_fix', 'isEnabled', true, false)
			) {
				havePublishedRef.current = false;
				return;
			}
			if (mode === EMBEDDED_CONFLUENCE_MODE.VIEW_MODE) {
				setConfluenceData({
					contentUrl,
					mode: EMBEDDED_CONFLUENCE_MODE.EDIT_MODE,
				});
			}
		}
		if (eventName === 'edit-page/publish' && action === 'taskSuccess') {
			if (expValEquals('cc_jira_cppp_publish_fix', 'isEnabled', true, false)) {
				havePublishedRef.current = true;
			}
			if (mode === EMBEDDED_CONFLUENCE_MODE.EDIT_MODE) {
				setConfluenceData({
					isDraft: false,
					contentUrl,
					mode: EMBEDDED_CONFLUENCE_MODE.VIEW_MODE,
				});
			}
		}
	}
};

export const useEmbeddedPanelEntryPoint = ():
	| {
			entryPointActions: EntryPointActions | undefined;
			entryPoint: React.JSX.Element | null;
			ref?: undefined;
	  }
	| {
			ref: undefined;
			entryPoint: null;
			entryPointActions?: undefined;
	  } => {
	const { locale } = useIntl();
	const [state, actions] = useEmbeddedConfluenceSidePanel();
	const {
		contentType,
		confluenceData: { contentId, contentUrl, hostname, isLive, isDraft, title, mode },
		isDeferSpaceSelection,
		contentActions: { onLinkPage },
		panel: { isOpen, isExpanded },
		embeddedConfluenceSource,
		analyticsSource,
	} = state;

	const isPageDraft = isDraft && contentType === CONTENT_TYPE.PAGE;

	const parentProduct = useParentProduct();

	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		embeddedPanelEntrypoint,
		entryPointParams,
	);

	const embeddedConfluenceSourceForPanel = `${embeddedConfluenceSource}Panel`;

	// The panel is mounted in the shared layout, outside the issue/work-item
	// provider tree. Once the issue-context mirror is removed the analytics source
	// is empty here, so behind `remove-issue-context-mirror-on-wiv` we use the
	// source captured from the writer at open time.
	const fireAnalyticsEvent = useEmbeddedPageTracking(
		fg('remove-issue-context-mirror-on-wiv') ? analyticsSource : undefined,
	);
	const themeState = useThemeObserver();
	const havePublishedRef = useRef(false);

	/*
		If the user is on a custom domain, we need to update the
		hostname to reflect the parentProduct's hostname. By providing the hostname
		prop we will force EP to use the parentProduct's hostname instead of the content's hostname
		when on a custom domain (see EP code here https://bitbucket.org/atlassian/atlassian-frontend-monorepo/src/master/platform/packages/confluence/embedded-confluence-common/src/page/parsePageProps.ts#75).
	*/
	const contentHostname = setHostnameForContent(contentUrl, hostname);

	const onContentAction = useCallback(
		(action: string, eventName: string) => {
			handleContentAction(action, eventName, {
				fireAnalyticsEvent,
				setConfluenceData: actions.setConfluenceData,
				contentUrl,
				embeddedConfluenceSourceForPanel,
				isOpen,
				mode,
				havePublishedRef,
			});
		},
		[actions, contentUrl, fireAnalyticsEvent, embeddedConfluenceSourceForPanel, isOpen, mode],
	);

	const onClose = useCallback(() => {
		fireAnalyticsEvent({
			action: 'clicked',
			eventName: 'closeEmbeddedConfluencePanel',
			embeddedConfluenceSource: embeddedConfluenceSourceForPanel,
		});
		entryPointActions.unload();

		actions.closeSidePanel();
	}, [actions, fireAnalyticsEvent, entryPointActions, embeddedConfluenceSourceForPanel]);
	const editAllowedFeatures = useMemo(
		() => [...(isDeferSpaceSelection ? ['publish-modal'] : []), 'template-browser'],
		[isDeferSpaceSelection],
	);
	const shouldApplyEditFeatures = editAllowedFeatures.length > 0 && isDraft;

	const resolvedEditAllowedFeatures = useMemo(() => {
		if (shouldApplyEditFeatures) return editAllowedFeatures;
		return undefined;
	}, [shouldApplyEditFeatures, editAllowedFeatures]);

	const shouldOpenPanel = isOpen && contentUrl;

	const {
		entryPointActions: epDraftModalActions,
		entryPointReferenceSubject: epDraftModalSubject,
	} = useEntryPoint(draftEmbeddedPageModalEntrypoint, {});

	const { entryPointActions: epModalActions, entryPointReferenceSubject: epModalSubject } =
		useEntryPoint(embeddedConfluencePageModalEntrypoint, {});

	const {
		entryPointActions: whiteboardModalActions,
		entryPointReferenceSubject: whiteboardModalSubject,
	} = useEntryPoint(embeddedWhiteboardModalEntrypoint, {});

	const {
		openContinueDraftModal,
		handleEmbeddedContentAction: handleDraftContentAction,
		epMode,
	} = useEmbeddedPageDraft({
		url: contentUrl,
		isLive,
		embeddedConfluenceSource,
		onEmbeddedContentAction: onContentAction,
		onClose,
		linkConfluencePage: onLinkPage,
	});

	const handleOnModalClose = useCallback(() => {
		actions.closeSidePanel();
		if (contentType === 'whiteboard') {
			whiteboardModalActions.unload();
		} else if (contentType === 'page') {
			isPageDraft ? epDraftModalActions.unload() : epModalActions.unload();
		}
	}, [
		actions,
		isPageDraft,
		epDraftModalActions,
		epModalActions,
		whiteboardModalActions,
		contentType,
	]);

	const runtimeProps = useMemo(
		() => ({
			locale,
			hasFooterLogo: false,
			isDraft,
			isLive,
			hostname: contentHostname,
			panelTitle: title,
			navigationPolicy: undefined,
			onClose,
			onContentAction,
			parentProduct,
			spaceKey: parseUrl(contentUrl)?.spaceKey,
			themeState,
			url: convertUrlByContentState({
				mode,
				isDraft,
				contentUrl,
				spaceKey: parseUrl(contentUrl)?.spaceKey,
			}),
			mode: isPageDraft ? epMode : mode,
			embeddedConfluenceSource: embeddedConfluenceSourceForPanel,
			contentId,
			contentType,
			onPublish: actions.setDeferredSpaceData,
			allowedFeatures: {
				view: [
					'byline-contributors',
					'byline-extensions',
					'page-comments',
					'page-reactions',
					'inline-comments',
					'non-licensed-share',
				],
				edit: resolvedEditAllowedFeatures,
			},
			linkConfluencePage: onLinkPage,
		}),
		[
			actions.setDeferredSpaceData,
			contentId,
			contentUrl,
			embeddedConfluenceSourceForPanel,
			contentHostname,
			isDraft,
			isLive,
			locale,
			title,
			mode,
			epMode,
			onClose,
			onContentAction,
			onLinkPage,
			parentProduct,
			resolvedEditAllowedFeatures,
			themeState,
			isPageDraft,
			contentType,
		],
	);

	const whiteboardModalRuntimeProps = useMemo(
		() => ({
			isOpen: true,
			locale,
			hasFooterLogo: false,
			hostname: contentHostname,
			panelTitle: title,
			navigationPolicy: undefined,
			enableContract: true,
			onClose,
			onEmbeddedContentAction: onContentAction,
			onContract: actions.toggleExpandedState,
			parentProduct,
			spaceKey: parseUrl(contentUrl)?.spaceKey,
			themeState,
			url: contentUrl,
			embeddedConfluenceSource: embeddedConfluenceSourceForPanel,
			contentId: contentId || parseUrl(contentUrl)?.contentId || '',
			contentType,
		}),
		[
			contentId,
			contentType,
			contentUrl,
			embeddedConfluenceSourceForPanel,
			contentHostname,
			locale,
			title,
			onClose,
			onContentAction,
			parentProduct,
			themeState,
			actions.toggleExpandedState,
		],
	);

	const epModalRuntimeProps = useMemo(
		() => ({
			locale,
			hasFooterLogo: false,
			isDraft,
			isOpen: true,
			enableContract: true,
			onClose: () => {
				isPageDraft ? openContinueDraftModal() : handleOnModalClose();
			},
			onContract: actions.toggleExpandedState,
			parentProduct,
			url: convertUrlByContentState({
				mode,
				isDraft,
				contentUrl,
				spaceKey: parseUrl(contentUrl)?.spaceKey,
			}),
			mode: isPageDraft ? epMode : mode,
			isLive,
			embeddedConfluenceSource,
			spaceKey: parseUrl(contentUrl)?.spaceKey,
			contentId: parseUrl(contentUrl)?.contentId,
			onPublish: actions.setDeferredSpaceData,
			allowedFeatures: {
				view: [
					'byline-contributors',
					'byline-extensions',
					'page-comments',
					'page-reactions',
					'inline-comments',
					'non-licensed-share',
				],
				edit: resolvedEditAllowedFeatures,
			},
			linkConfluencePage: onLinkPage,
			themeState,
			onPageAction: isPageDraft ? handleDraftContentAction : onContentAction,
			navigationPolicy: undefined,
			hostname: contentHostname,
			isFullScreenEmbedView: true,
		}),
		[
			locale,
			isDraft,
			actions.toggleExpandedState,
			actions.setDeferredSpaceData,
			parentProduct,
			isPageDraft,
			mode,
			epMode,
			contentUrl,
			isLive,
			embeddedConfluenceSource,
			resolvedEditAllowedFeatures,
			themeState,
			handleDraftContentAction,
			onContentAction,
			contentHostname,
			openContinueDraftModal,
			handleOnModalClose,
			onLinkPage,
		],
	);

	const setEntrypointActions = () => {
		if (!isExpanded) {
			return entryPointActions;
		}
		if (contentType === 'whiteboard') {
			return whiteboardModalActions;
		}
		if (contentType === 'page') {
			return isDraft ? epDraftModalActions : epModalActions;
		}
	};

	const setExpandedEntryPointData = () => {
		if (contentType === 'page') {
			if (isDraft) {
				return {
					id: 'epDraftModalEntrypointContainer',
					entryPointReferenceSubject: epDraftModalSubject,
					runtimeProps: epModalRuntimeProps,
				};
			}
			return {
				id: 'epModalEntrypointContainer',
				entryPointReferenceSubject: epModalSubject,
				runtimeProps: epModalRuntimeProps,
			};
		}
		if (contentType === 'whiteboard') {
			return {
				id: 'embeddedWhiteboardModalEntrypointContainer',
				entryPointReferenceSubject: whiteboardModalSubject,
				runtimeProps: whiteboardModalRuntimeProps,
			};
		}
	};

	const entryPointData = setExpandedEntryPointData();

	const expandedEntryPointContainer = () => {
		if (contentType === 'page') {
			if (isDraft) {
				return (
					<JiraEntryPointContainer
						entryPointReferenceSubject={epDraftModalSubject}
						id="epDraftModalEntrypointContainer"
						packageName="@jira/issue__create-confluence-content"
						teamName="confluence-better-together"
						runtimeProps={epModalRuntimeProps}
					/>
				);
			}

			return (
				<JiraEntryPointContainer
					entryPointReferenceSubject={epModalSubject}
					id="epModalEntrypointContainer"
					packageName="@jira/issue__create-confluence-content"
					teamName="confluence-better-together"
					runtimeProps={epModalRuntimeProps}
				/>
			);
		}
		if (contentType === 'whiteboard') {
			return (
				<JiraEntryPointContainer
					entryPointReferenceSubject={whiteboardModalSubject}
					id="embeddedWhiteboardModalEntrypointContainer"
					packageName="@jira/issue__create-confluence-content"
					teamName="confluence-better-together"
					runtimeProps={whiteboardModalRuntimeProps}
				/>
			);
		}
		return null;
	};

	return shouldOpenPanel && entryPointData
		? {
				entryPointActions: setEntrypointActions(),
				entryPoint: isExpanded ? (
					expandedEntryPointContainer()
				) : (
					<JiraEntryPointContainer
						entryPointReferenceSubject={entryPointReferenceSubject}
						id="epPanelEntrypointContainer"
						packageName="@jira/issue__create-confluence-content"
						teamName="confluence-better-together"
						runtimeProps={runtimeProps}
					/>
				),
			}
		: { ref: undefined, entryPoint: null };
};
